<!--
  ChangeStoreOwnerDialog -- move a store to another teammate.

  Card: Team & Permissions (https://trello.com/c/IlgSKtZl), deactivate
  pebble. Store ownership IS stores.created_by_id (it also scopes a Builder's
  / Sales Executive's store list). Deactivating a teammate hands their stores
  to the acting Owner; this is how the Owner moves each one on.

  Options are the distributor's ACTIVE teammates from GET
  /distributor/teamMembers (pending invitees cannot own a store), minus the
  current owner. Save POSTs /distributor/stores/<id>/owner; that route is
  Owner-only and re-checks tenant, activity and that the role can own
  stores, so a refusal is shown inline and the dialog stays open.

  ASCII-only. Selectors use data-testid.
-->
<template>
  <v-dialog
    :model-value="modelValue"
    persistent
    max-width="480px"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card data-testid="store-owner-dialog">
      <v-card-title>
        <div class="d-flex align-center">
          <h1>Change store owner</h1>
          <v-spacer />
          <v-btn icon variant="plain" @click="close">
            <v-icon icon="close" />
          </v-btn>
        </div>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <div
          v-if="error"
          data-testid="store-owner-error"
          class="cso-error"
          role="alert"
        >
          {{ error }}
        </div>
        <div class="mb-3">
          Who should own <strong>{{ storeName || `store #${storeId}` }}</strong>?
          They will see it in their store list and can work on it.
        </div>
        <v-select
          v-model="selectedId"
          :items="options"
          :loading="loading"
          label="New owner"
          data-testid="store-owner-select"
        />
      </v-card-text>
      <v-card-actions class="py-3 px-3">
        <v-btn variant="flat" data-testid="store-owner-cancel" @click="close">
          Cancel
        </v-btn>
        <v-spacer />
        <v-btn
          variant="flat"
          color="primary"
          class="text-white"
          data-testid="store-owner-confirm"
          :disabled="!selectedId || saving"
          :loading="saving"
          @click="save"
        >
          Change owner
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ChangeStoreOwnerDialog",

  props: {
    modelValue: { type: Boolean, default: false },
    storeId: { type: Number, required: true },
    storeName: { type: String, default: "" },
    // Excluded from the options when known (a deep link may not know it).
    currentOwnerId: { type: Number, default: null },
  },

  emits: ["update:modelValue", "changed"],

  data() {
    return {
      members: [],
      selectedId: null,
      loading: false,
      saving: false,
      error: null,
    };
  },

  computed: {
    options() {
      return this.members
        .filter((m) => m.status === "active" && m.id !== this.currentOwnerId)
        .map((m) => ({ title: m.name || m.email, value: m.id }));
    },
  },

  async mounted() {
    this.loading = true;
    try {
      const response = await this.$axios.get("/distributor/teamMembers");
      this.members = Array.isArray(response && response.data) ? response.data : [];
    } catch (err) {
      this.error = "Could not load your teammates.";
    } finally {
      this.loading = false;
    }
  },

  methods: {
    close() {
      this.$emit("update:modelValue", false);
    },
    async save() {
      if (!this.selectedId) return;
      this.saving = true;
      this.error = null;
      try {
        const response = await this.$axios.post(
          `/distributor/stores/${this.storeId}/owner`,
          { account_id: this.selectedId }
        );
        this.$emit("changed", response.data);
        this.close();
      } catch (err) {
        const data = err && err.response ? err.response.data : null;
        this.error = (data && data.error) || "Could not change the store owner.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.cso-error {
  margin-bottom: var(--tf-sp-3, 12px);
  padding: var(--tf-sp-2, 8px) var(--tf-sp-3, 12px);
  border-radius: var(--tf-radius-xs, 4px);
  background: #fdecec;
  color: var(--tf-error, #d32f2f);
  font-size: var(--tf-size-caption, 0.8125rem);
}
</style>
